import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit2, X, RefreshCw, FileText, Trash2, ArrowRightLeft, Download } from 'lucide-react';
import { 
  CopyButton, ColoredAmount, LoadingButton, 
  validateRequired, findDuplicateClient, DuplicateWarning,
  ConfirmDialog
} from '../components/QuickWins';

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    base_currency: 'INR',
    cycle_preference: 'monthly',
    status: 'active',
    exclude_from_cycle: false,
    reinvest_profits: false,
    joining_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [duplicateClients, setDuplicateClients] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, client: null });
  
  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchClients();
  }, []);

  const [clientsData, setClientsData] = useState({
    clients: [],
    conversion_rate: 94,
    total_aum_inr: 0,
    total_inr_pool: 0,
    total_usdt_pool: 0
  });

  // Convert to USDT modal state
  const [convertModal, setConvertModal] = useState({ show: false, client: null });
  const [convertData, setConvertData] = useState({
    amount_inr: '',
    conversion_rate: '',
    notes: ''
  });
  const [isConverting, setIsConverting] = useState(false);

  const [demoStatus, setDemoStatus] = useState({ loaded: false, client_id: null });

  const fetchDemoStatus = async () => {
    try {
      const r = await axiosInstance.get('/admin/demo-mode/status');
      setDemoStatus(r.data || { loaded: false });
    } catch (e) { /* non-admin users get 403 — ignore */ }
  };

  useEffect(() => { fetchDemoStatus(); }, []);

  const toggleDemoMode = async () => {
    try {
      if (demoStatus.loaded) {
        if (!window.confirm('Clear demo data? This removes the demo client and its history.')) return;
        await axiosInstance.post('/admin/demo-mode/clear');
        toast.success('Demo data cleared');
      } else {
        const r = await axiosInstance.post('/admin/demo-mode/load');
        toast.success(`Demo loaded: ${r.data.client_code}. Open its v3 report to preview.`);
      }
      await fetchDemoStatus();
      await fetchClients();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Demo mode action failed');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get('/clients');
      setClientsData(response.data);
      setClients(response.data.clients);
    } catch (error) {
      console.error('Clients fetch error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to fetch clients. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkForDuplicates = () => {
    // Only check for new clients, not edits
    if (editingClient) return false;
    
    const duplicates = findDuplicateClient(
      { name: formData.name, client_code: formData.client_code },
      clients
    );
    
    if (duplicates.length > 0) {
      const duplicatesWithDisplay = duplicates.map(dup => ({
        ...dup,
        display: `${dup.client_code} - ${dup.name}`
      }));
      setDuplicateClients(duplicatesWithDisplay);
      return true;
    }
    
    setDuplicateClients([]);
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate name
    const nameValidation = validateRequired(formData.name, 'Client name');
    if (!nameValidation.valid) {
      toast.error(nameValidation.error);
      return;
    }
    
    // Check for duplicates
    if (checkForDuplicates()) {
      setShowDuplicateWarning(true);
      return;
    }
    
    await submitClient();
  };

  const handleProceedWithDuplicate = async () => {
    setShowDuplicateWarning(false);
    await submitClient();
  };

  const submitClient = async () => {
    setIsSubmitting(true);
    try {
      if (editingClient) {
        await axiosInstance.put(`/clients/${editingClient.id}`, formData);
        toast.success('Client updated successfully');
      } else {
        await axiosInstance.post('/clients', formData);
        toast.success('Client created successfully');
      }
      fetchClients();
      closeForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openForm = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name,
        base_currency: client.base_currency,
        cycle_preference: client.cycle_preference,
        status: client.status,
        exclude_from_cycle: client.exclude_from_cycle,
        reinvest_profits: client.reinvest_profits || false,
        joining_date: client.joining_date || new Date().toISOString().split('T')[0],
        notes: client.notes || ''
      });
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingClient(null);
    setDuplicateClients([]);
    setShowDuplicateWarning(false);
    setFormData({
      name: '',
      base_currency: 'INR',
      cycle_preference: 'monthly',
      status: 'active',
      exclude_from_cycle: false,
      reinvest_profits: false,
      joining_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const handleDeleteClient = async () => {
    if (!deleteConfirm.client) return;
    
    try {
      await axiosInstance.delete(`/clients/${deleteConfirm.client.id}`);
      toast.success(`Client ${deleteConfirm.client.client_code} deleted successfully`);
      setDeleteConfirm({ show: false, client: null });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const openConvertModal = (client) => {
    setConvertModal({ show: true, client });
    // Pre-fill with client's current AUM and latest conversion rate
    setConvertData({
      amount_inr: client.aum || '',
      conversion_rate: conversion_rate || 94,
      notes: ''
    });
  };

  const handleConvertToUSDT = async (e) => {
    e.preventDefault();
    if (!convertModal.client) return;
    
    const amount = parseFloat(convertData.amount_inr);
    const rate = parseFloat(convertData.conversion_rate);
    
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!rate || rate <= 0) {
      toast.error('Please enter a valid conversion rate');
      return;
    }
    
    setIsConverting(true);
    try {
      const response = await axiosInstance.post(`/clients/${convertModal.client.id}/convert-to-usdt`, {
        amount_inr: amount,
        conversion_rate: rate,
        notes: convertData.notes
      });
      
      toast.success(response.data.message);
      setConvertModal({ show: false, client: null });
      setConvertData({ amount_inr: '', conversion_rate: '', notes: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert currency');
    } finally {
      setIsConverting(false);
    }
  };

  // Use data from API response (includes latest cycle conversion rate)
  const { conversion_rate, total_aum_inr, total_inr_pool, total_usdt_pool } = clientsData;

  // Display-only abbreviation for the Total AUM header (INR figure). No calc change.
  const formatInrAbbrev = (value) => {
    if (value === null || value === undefined) return '0';
    if (Math.abs(value) >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
    if (Math.abs(value) >= 100000) return `${(value / 100000).toFixed(2)} L`;
    return value.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="clients-page">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Relationships</p>
            <h1 className="cb-h1">Clients</h1>
            <p className="cb-section-sub cb-num" style={{ marginTop: 'var(--cb-space-2)' }}>
              Total AUM: <span style={{ color: 'var(--cb-ink)', fontWeight: 500 }} title={`₹${total_aum_inr?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}>₹{formatInrAbbrev(total_aum_inr)}</span>
              <span className="cb-muted" style={{ marginLeft: 'var(--cb-space-2)' }}>
                (INR: ₹{total_inr_pool?.toLocaleString()} + USDT: {total_usdt_pool?.toLocaleString()} × {conversion_rate})
              </span>
            </p>
          </div>
          <div className="flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
            <button
              onClick={toggleDemoMode}
              data-testid="demo-mode-toggle"
              className={`cb-btn-secondary ${demoStatus.loaded ? 'cb-btn-secondary--active' : ''}`}
              title={demoStatus.loaded ? 'Clear demo data' : 'Load a richly-populated demo client'}
            >
              {demoStatus.loaded ? '✓ Demo Loaded · Clear' : 'Load Demo Mode'}
            </button>
            <button
              onClick={() => openForm()}
              data-testid="add-client-button"
              className="cb-btn-primary"
            >
              <Plus className="w-4 h-4" />
              Add Client
            </button>
          </div>
        </div>
        <hr className="cb-divider" />
      </div>

      {/* Client List */}
      <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
        <table data-testid="clients-table" className="cb-table" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th className="whitespace-nowrap">Code</th>
              <th className="whitespace-nowrap">Name</th>
              <th className="whitespace-nowrap">Joined</th>
              <th className="whitespace-nowrap">Currency</th>
              <th className="whitespace-nowrap cb-num">AUM</th>
              <th className="whitespace-nowrap cb-num">% Total</th>
              <th className="whitespace-nowrap cb-num">Payouts</th>
              <th className="whitespace-nowrap" style={{ textAlign: 'center' }}>Reinvest</th>
              <th className="whitespace-nowrap">Status</th>
              <th className="whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center cb-muted">No clients found</td>
              </tr>
            ) : (
              clients.map((client) => (
                <tr key={client.id}>
                  <td className="whitespace-nowrap" style={{ fontWeight: 500 }}>
                    {client.client_code}
                    <CopyButton text={client.client_code} label="Client Code" />
                  </td>
                  <td className="whitespace-nowrap">{client.name}</td>
                  <td className="whitespace-nowrap cb-muted cb-num" style={{ fontSize: '0.8125rem' }}>{client.joining_date || '-'}</td>
                  <td className="whitespace-nowrap">{client.base_currency}</td>
                  <td className="whitespace-nowrap cb-num">
                    {(client.base_currency === 'INR' ? '₹' : '')}{(client.aum || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </td>
                  <td className="whitespace-nowrap cb-num cb-muted">
                    {client.aum_percent?.toFixed(2) || '0.00'}%
                  </td>
                  <td className="whitespace-nowrap cb-num">
                    {(client.base_currency === 'INR' ? '₹' : '')}{(client.total_payouts || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {client.reinvest_profits ? (
                      <span className="cb-pill cb-pill--ink" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <RefreshCw className="w-3 h-3" />
                        Yes
                      </span>
                    ) : (
                      <span className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px' }}>No</span>
                    )}
                  </td>
                  <td>
                    <span className={`cb-pill ${client.status === 'active' ? 'cb-pill--positive' : ''}`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap" style={{ display: 'flex', gap: 'var(--cb-space-2)', alignItems: 'center' }}>
                    <button
                      onClick={() => navigate(`/client-report-v3/${client.id}`)}
                      data-testid={`view-report-v3-${client.client_code}`}
                      title="View Editorial Report (v3)"
                      style={{
                        fontFamily: 'var(--cb-serif)',
                        fontStyle: 'italic',
                        fontSize: '0.875rem',
                        color: 'var(--cb-ink)',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px 4px',
                      }}
                    >
                      v3*
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const res = await axiosInstance.get(`/clients/${client.id}/ledger/csv`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `ledger_${client.client_code}.csv`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (e) {
                          alert('Failed to download ledger CSV');
                        }
                      }}
                      data-testid={`download-ledger-csv-${client.client_code}`}
                      title="Download Ledger CSV"
                      style={{ color: 'var(--cb-ink-soft)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                    >
                      <Download className="w-4 h-4 inline" />
                    </button>
                    <button
                      onClick={() => openForm(client)}
                      data-testid={`edit-client-${client.client_code}`}
                      title="Edit Client"
                      style={{ color: 'var(--cb-ink-soft)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                    >
                      <Edit2 className="w-4 h-4 inline" />
                    </button>
                    {currentUser.is_admin && client.base_currency === 'INR' && (
                      <button
                        onClick={() => openConvertModal(client)}
                        data-testid={`convert-client-${client.client_code}`}
                        title="Convert INR to USDT"
                        style={{ color: 'var(--cb-ink-soft)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                      >
                        <ArrowRightLeft className="w-4 h-4 inline" />
                      </button>
                    )}
                    {currentUser.is_admin && (
                      <button
                        onClick={() => setDeleteConfirm({ show: true, client })}
                        data-testid={`delete-client-${client.client_code}`}
                        title="Delete Client"
                        style={{ color: 'var(--cb-negative)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">{editingClient ? 'Edit Client' : 'Add Client'}</h2>
              <button onClick={closeForm} data-testid="close-form-button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Duplicate Warning */}
              {showDuplicateWarning && duplicateClients.length > 0 && (
                <DuplicateWarning 
                  duplicates={duplicateClients}
                  type="client"
                  onProceed={handleProceedWithDuplicate}
                  onCancel={() => setShowDuplicateWarning(false)}
                />
              )}

              <div>
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  data-testid="client-name-input"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="form-label">Joining Date *</label>
                <input
                  type="date"
                  data-testid="client-joining-date-input"
                  className="form-input"
                  value={formData.joining_date}
                  onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="form-label">Base Currency *</label>
                <select
                  data-testid="client-currency-select"
                  className="form-input"
                  value={formData.base_currency}
                  onChange={(e) => setFormData({ ...formData, base_currency: e.target.value })}
                >
                  <option value="INR">INR</option>
                  <option value="USDT">USDT</option>
                </select>
              </div>

              <div>
                <label className="form-label">Cycle Preference *</label>
                <select
                  data-testid="client-cycle-select"
                  className="form-input"
                  value={formData.cycle_preference}
                  onChange={(e) => setFormData({ ...formData, cycle_preference: e.target.value })}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              <div>
                <label className="form-label">Status *</label>
                <select
                  data-testid="client-status-select"
                  className="form-input"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  data-testid="client-exclude-checkbox"
                  id="exclude_from_cycle"
                  checked={formData.exclude_from_cycle}
                  onChange={(e) => setFormData({ ...formData, exclude_from_cycle: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="exclude_from_cycle" className="text-sm" style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>
                  Exclude from Cycle
                </label>
              </div>

              <div className="flex items-center" style={{ background: 'var(--cb-bone)', padding: 'var(--cb-space-3)', borderRadius: 'var(--cb-radius)', border: '1px solid var(--cb-line)' }}>
                <input
                  type="checkbox"
                  data-testid="client-reinvest-checkbox"
                  id="reinvest_profits"
                  checked={formData.reinvest_profits}
                  onChange={(e) => setFormData({ ...formData, reinvest_profits: e.target.checked })}
                  className="mr-2"
                />
                <div>
                  <label htmlFor="reinvest_profits" className="text-sm flex items-center" style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Reinvest Profits
                  </label>
                  <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                    Profits will be added to capital instead of payout
                  </p>
                </div>
              </div>

              <div>
                <label className="form-label">Notes</label>
                <textarea
                  data-testid="client-notes-input"
                  className="form-input"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              <div className="flex gap-3">
                <LoadingButton 
                  type="submit" 
                  data-testid="submit-client-form" 
                  className="cb-btn-primary"
                  style={{ flex: 1, justifyContent: 'center' }}
                  loading={isSubmitting}
                >
                  {editingClient ? 'Update' : 'Create'}
                </LoadingButton>
                <button type="button" onClick={closeForm} className="cb-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, client: null })}
        onConfirm={handleDeleteClient}
        title="Delete Client"
        message={`Are you sure you want to delete client "${deleteConfirm.client?.client_code} - ${deleteConfirm.client?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Convert to USDT Modal */}
      {convertModal.show && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Convert INR to USDT</h2>
              <button onClick={() => setConvertModal({ show: false, client: null })} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4" style={{ padding: 'var(--cb-space-3)', background: 'var(--cb-bone)', borderRadius: 'var(--cb-radius)', border: '1px solid var(--cb-line)' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                <strong>{convertModal.client?.client_code}</strong> - {convertModal.client?.name}
              </p>
              <p className="cb-muted cb-num" style={{ fontSize: '0.8125rem', marginTop: 'var(--cb-space-1)' }}>
                Current Balance: ₹{convertModal.client?.aum?.toLocaleString()}
              </p>
            </div>
            
            <form onSubmit={handleConvertToUSDT} className="space-y-4">
              <div>
                <label className="form-label">
                  Amount to Convert (INR) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={convertData.amount_inr}
                  onChange={(e) => setConvertData({ ...convertData, amount_inr: e.target.value })}
                  className="input-field"
                  placeholder="Enter INR amount"
                  required
                />
                <p className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                  Max: ₹{convertModal.client?.aum?.toLocaleString()}
                </p>
              </div>
              
              <div>
                <label className="form-label">
                  Conversion Rate (INR per USDT) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={convertData.conversion_rate}
                  onChange={(e) => setConvertData({ ...convertData, conversion_rate: e.target.value })}
                  className="input-field"
                  placeholder="e.g., 94"
                  required
                />
                <p className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                  Latest cycle rate: {conversion_rate}
                </p>
              </div>
              
              {convertData.amount_inr && convertData.conversion_rate && (
                <div style={{ padding: 'var(--cb-space-3)', background: 'var(--cb-bone)', borderRadius: 'var(--cb-radius)', border: '1px solid var(--cb-line)' }}>
                  <p className="cb-num" style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                    <strong>USDT to be credited:</strong>{' '}
                    {(parseFloat(convertData.amount_inr) / parseFloat(convertData.conversion_rate)).toFixed(2)} USDT
                  </p>
                </div>
              )}
              
              <div>
                <label className="form-label">
                  Notes (Optional)
                </label>
                <textarea
                  value={convertData.notes}
                  onChange={(e) => setConvertData({ ...convertData, notes: e.target.value })}
                  className="input-field"
                  rows="2"
                  placeholder="Reason for conversion..."
                />
              </div>
              
              <div className="flex space-x-3 pt-2">
                <LoadingButton
                  type="submit"
                  className="cb-btn-primary"
                  style={{ flex: 1, justifyContent: 'center' }}
                  loading={isConverting}
                  data-testid="confirm-convert-btn"
                >
                  Convert to USDT
                </LoadingButton>
                <button 
                  type="button" 
                  onClick={() => setConvertModal({ show: false, client: null })} 
                  className="cb-btn-secondary"
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
